import { Box, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "common/locales";
import { useArea } from "common/utils";
import {
  getTotalAvailableDryMatterForPasturesFromArrays,
  getTotalAvgDMFromArray,
  getTotalFarmAnimalCountFromArray,
  getTotalFarmAreaFromArrays,
  getTotalFieldCountFromArrays,
  useArableFields,
  useCurrentFarmAnimalGroups,
  useCurrentFarmStockClasses,
  useFields,
} from "model";
import { useUIStore } from "stores";

const BELLFLOWER_COLOR = "#D3C9E7";

interface MetricItemProps {
  label: string;
  value: string | number;
  unit?: string;
}

function MetricItem({ label, value, unit }: MetricItemProps) {
  return (
    <Tooltip title={String(value)} enterDelay={500} enterNextDelay={500}>
      <Box
        sx={{
          py: 0.75,
          borderBottom: "1px solid #F0F0F0",
          "&:last-child": {
            borderBottom: "none",
          },
          minWidth: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", fontSize: "0.7rem" }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "#1a1a2e",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
          {unit && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.7rem",
                flexShrink: 0,
              }}
            >
              {unit}
            </Typography>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
}

interface MetricsCardProps {
  children: React.ReactNode;
}

function MetricsCard({ children }: MetricsCardProps) {
  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 2,
        px: 1.5,
        py: 0.5,
        border: `1px solid ${BELLFLOWER_COLOR}`,
      }}
    >
      {children}
    </Box>
  );
}

export default function FarmMetricsCards() {
  const { t } = useTranslation();
  const { fromHa, label: areaUnitLabel } = useArea();
  const { data: fields = [] } = useFields();
  const { data: arableFields = [] } = useArableFields();
  const { data: animalGroups } = useCurrentFarmAnimalGroups();
  const { data: stockClasses } = useCurrentFarmStockClasses();

  const farmDryMatterNow = getTotalAvgDMFromArray(fields);
  const totalAnimals = getTotalFarmAnimalCountFromArray(stockClasses);
  const totalFieldCount = getTotalFieldCountFromArrays(fields, arableFields);
  const totalHectares = getTotalFarmAreaFromArrays(fields, arableFields);
  const growthRate = useUIStore((state) => state.growthRate);
  const totalAvailableDM = getTotalAvailableDryMatterForPasturesFromArrays(
    fields,
    animalGroups
  );

  // Check if we have any meaningful data to display
  const hasAnimals = totalAnimals !== undefined && totalAnimals > 0;
  const hasFields = totalFieldCount !== undefined && totalFieldCount > 0;
  const hasArea = totalHectares !== undefined && totalHectares > 0;
  const hasDryMatter =
    typeof farmDryMatterNow === "number" &&
    !isNaN(farmDryMatterNow) &&
    farmDryMatterNow > 0;
  const hasGrowthRate =
    typeof growthRate === "number" && !isNaN(growthRate) && growthRate > 0;
  const hasAvailableDM = totalAvailableDM > 0;

  // Check if first card has any content
  const hasFirstCardContent = hasAnimals || hasArea || hasFields;
  // Check if second card has any content
  const hasSecondCardContent = hasDryMatter || hasGrowthRate || hasAvailableDM;

  // If no data at all, don't render the cards
  if (!hasFirstCardContent && !hasSecondCardContent) {
    return null;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1,
        }}
      >
        {/* First card: Animals, Total Ha, Fields */}
        {hasFirstCardContent && (
          <MetricsCard>
            {hasAnimals && (
              <MetricItem
                label={t("farm.animals")}
                value={totalAnimals.toLocaleString()}
              />
            )}
            {hasArea && (
              <MetricItem
                label={t("farm.totalArea")}
                value={(
                  Math.round(fromHa(totalHectares) * 100) / 100
                ).toLocaleString()}
                unit={areaUnitLabel}
              />
            )}
            {hasFields && (
              <MetricItem
                label={t("farm.fields")}
                value={totalFieldCount.toLocaleString()}
              />
            )}
          </MetricsCard>
        )}

        {/* Second card: Total Cover Average, Growth Rate */}
        {hasSecondCardContent && (
          <MetricsCard>
            {hasDryMatter && (
              <MetricItem
                label={t("farm.totalDM.label")}
                value={farmDryMatterNow.toLocaleString()}
                unit={t("farm.totalDM.unit")}
              />
            )}
            {hasGrowthRate && (
              <MetricItem
                label={t("farm.growth.label")}
                value={Math.round(growthRate).toLocaleString()}
                unit={t("farm.growth.unit")}
              />
            )}
            {hasAvailableDM && (
              <MetricItem
                label={t("farm.availableDM.label")}
                value={(totalAvailableDM / 1000).toFixed(1)}
                unit={t("farm.availableDM.unit")}
              />
            )}
          </MetricsCard>
        )}
      </Box>
    </Box>
  );
}
